import { TagRenderer } from '@med-fe/ui';
import {
  DeleteOutlined,
  EditOutlined,
  MoreVertOutlined,
  ThumbDownAltOutlined,
  ThumbUpAltOutlined,
} from '@mui/icons-material';
import { Button, Popover, Tooltip } from 'antd';
import { DayOfWeek } from 'apps/mod/src/models/day-enum';
import moment from 'moment';
import './grid-column-style.css';

function capitalizeFirstLetter(val) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

export const hooColumnOrder = [
  'ScheduleChangeRequestId',
  'Requestor',
  'CreatedDate',
  'EffectiveDate',
  'Location',
  'Status',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
  'Reason',
  'Notes',
  'RejectReason', // Make sure this matches the actual accessorKey or id
  'Actions', // Also must match the column's id
];

export default function hooAccommodationsColumns(
  userEmailAddress: string,
  isDefaultScheduleChangeRequestor: boolean,
  hasHooAccommodationWritePermission: boolean,
  statuses: string[],
  editDrawer?: ((record: any) => void) | any,
  deleteTemplate?: ((record: any) => void) | any,
  onRequestAccept?: (record: any) => void | any,
  onRequestDecline?: (record: any) => void | any
) {
  const hooAccommodationsColumns = [
    {
      id: 'ScheduleChangeRequestId',
      header: 'REQUEST ID',
      accessorKey: 'ScheduleChangeRequestId',
      enableColumnFilter: false,
      size: 120,
    },
    {
      id: 'Requestor',
      header: 'REQUESTOR',
      accessorKey: 'Requestor',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 120,
    },
    {
      id: 'CreatedDate',
      header: 'REQUEST DATE',
      accessorKey: 'CreatedDate',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 140,
      Cell: ({ row }) => moment(row.original.CreatedDate).format('YYYY-MM-DD'), // Format the date
    },
    {
      id: 'EffectiveDate',
      header: 'EFFECTIVE DATE',
      accessorKey: 'EffectiveDate',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 140,
      Cell: ({ row }) => moment(row.original.EffectiveDate).format('YYYY-MM-DD'), // Format the date
    },
    {
      id: 'Location',
      header: 'LOCATION',
      accessorKey: 'Location',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 120,
    },
    {
      id: 'Status',
      header: 'STATUS',
      accessorKey: 'Status',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 120,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return <TagRenderer label={value} value={value} closable={false} onClose={() => {}} />;
      },
    },

    {
      id: capitalizeFirstLetter(DayOfWeek.Monday),
      header: DayOfWeek.Monday.toUpperCase(),
      accessorKey: capitalizeFirstLetter(DayOfWeek.Monday),
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 130,
    },
    {
      id: capitalizeFirstLetter(DayOfWeek.Tuesday),
      header: DayOfWeek.Tuesday.toUpperCase(),
      accessorKey: capitalizeFirstLetter(DayOfWeek.Tuesday),
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 130,
    },
    {
      id: capitalizeFirstLetter(DayOfWeek.Wednesday),
      header: DayOfWeek.Wednesday.toUpperCase(),
      accessorKey: capitalizeFirstLetter(DayOfWeek.Wednesday),
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 130,
    },
    {
      id: capitalizeFirstLetter(DayOfWeek.Thursday),
      header: DayOfWeek.Thursday.toUpperCase(),
      accessorKey: capitalizeFirstLetter(DayOfWeek.Thursday),
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 130,
    },
    {
      id: capitalizeFirstLetter(DayOfWeek.Friday),
      header: DayOfWeek.Friday.toUpperCase(),
      accessorKey: capitalizeFirstLetter(DayOfWeek.Friday),
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 130,
    },
    {
      id: capitalizeFirstLetter(DayOfWeek.Saturday),
      header: DayOfWeek.Saturday.toUpperCase(),
      accessorKey: capitalizeFirstLetter(DayOfWeek.Saturday),
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 130,
    },
    {
      id: capitalizeFirstLetter(DayOfWeek.Sunday),
      header: DayOfWeek.Sunday.toUpperCase(),
      accessorKey: capitalizeFirstLetter(DayOfWeek.Sunday),
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 130,
    },

    {
      id: 'Reason',
      header: 'REASON',
      accessorKey: 'Reason',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 120,
    },
    {
      id: 'Notes',
      header: 'NOTES',
      accessorKey: 'Notes',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 250,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return (
          <Tooltip
            title={value || ''}
            placement='bottom'
            color={'white'}
            overlayInnerStyle={{ color: '#003366' }}
            overlayStyle={{ fontSize: '10px' }}
          >
            <div
              style={{
                maxWidth: 200,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'default',
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const RejectReasonColumn = {
    id: 'RejectReason',
    header: 'REJECTION REASON',
    accessorKey: 'RejectReason',
    enableColumnFilter: true,
    filterFn: 'contains',
    size: 250,
    Cell: ({ cell }) => {
      const value = cell.getValue();
      return (
        <Tooltip
          title={value || ''}
          placement='bottom'
          color={'white'}
          overlayInnerStyle={{ color: '#003366' }}
          overlayStyle={{ fontSize: '10px' }}
        >
          <div
            style={{
              maxWidth: 200,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'default',
            }}
          >
            {value}
          </div>
        </Tooltip>
      );
    },
  };

  const actionColumn = {
    accessorKey: 'Actions',
    header: 'ACTIONS',
    id: 'Actions', // Make sure to set the id if accessorKey is not provided
    size: 150,
    enableColumnFilter: false,
    enableSorting: false,
    Cell: ({ cell }: { cell: any }) => {
      return (
        <>
          <div className='d-flex w-100' style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {cell?.row?.original.Status?.toUpperCase() === 'PENDING' && (
              <>
                {(isDefaultScheduleChangeRequestor ||
                  userEmailAddress == cell?.row?.original.RequestorEmail ||
                  userEmailAddress == cell?.row?.original.CreatedByEmail) && (
                  <>
                    <Tooltip placement='top' title='Edit'>
                      <Button
                        style={{
                          backgroundColor: '#003366',
                          border: 'none',
                          borderRadius: '5px',
                          width: '24px',
                          height: '25px',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                        icon={
                          <EditOutlined
                            sx={{ color: 'white', fontSize: 20, marginLeft: '10px', marginRight: '10px' }}
                          />
                        }
                        onClick={() => editDrawer(cell?.row?.original)}
                      />
                    </Tooltip>
                    <Tooltip placement='top' title='Delete'>
                      <Button
                        style={{
                          backgroundColor: '#d14d4d',
                          border: 'none',
                          borderRadius: '5px',
                          width: '24px',
                          height: '25px',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                        icon={
                          <DeleteOutlined
                            sx={{ color: 'white', fontSize: 20, marginLeft: '10px', marginRight: '10px' }}
                          />
                        }
                        onClick={() => {
                          deleteTemplate(cell?.row?.original.ScheduleChangeRequestId);
                        }}
                      />
                    </Tooltip>
                  </>
                )}
                {isDefaultScheduleChangeRequestor && (
                  <Popover
                    placement='bottomRight'
                    content={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div
                          className='action-flex-center'
                          onClick={() => {
                            if (onRequestAccept) onRequestAccept(cell?.row?.original.ScheduleChangeRequestId);
                          }}
                        >
                          <Button
                            type='text'
                            style={{
                              backgroundColor: '#008000',
                              border: 'none',
                              borderRadius: '5px',
                              width: '24px',
                              height: '25px',
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                            icon={
                              <ThumbUpAltOutlined
                                sx={{ color: 'white', fontSize: 20, marginLeft: '10px', marginRight: '10px' }}
                              />
                            }
                          />
                          <div>Accept</div>
                        </div>
                        <div
                          className='action-flex-center'
                          onClick={() => {
                            if (onRequestDecline) onRequestDecline(cell?.row?.original.ScheduleChangeRequestId);
                          }}
                        >
                          <Button
                            type='text'
                            style={{
                              backgroundColor: '#d14d4d',
                              border: 'none',
                              borderRadius: '5px',
                              width: '24px',
                              height: '25px',
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                            icon={
                              <ThumbDownAltOutlined
                                sx={{ color: 'white', fontSize: 20, marginLeft: '10px', marginRight: '10px' }}
                              />
                            }
                          />
                          <div>Reject</div>
                        </div>
                      </div>
                    }
                  >
                    <MoreVertOutlined fontSize='medium' style={{ marginLeft: '0px' }} />
                  </Popover>
                )}
              </>
            )}
          </div>
        </>
      );
    },
  };

  let showActions = hasHooAccommodationWritePermission;

  if (hasHooAccommodationWritePermission && Array.isArray(statuses) && statuses.length > 0) {
    if (typeof statuses[0] === 'string') {
      showActions = statuses.includes('Pending') || statuses.includes('PENDING');
    } else {
      showActions = statuses.some(
        (row: any) =>
          Array.isArray(row.statuses) && (row.statuses.includes('Pending') || row.statuses.includes('PENDING'))
      );
    }
  }

  let showRejectReasonColumn = true;
  showRejectReasonColumn = statuses && statuses.includes('REJECTED');

  const columnsWithRejectReason = showRejectReasonColumn
    ? [...hooAccommodationsColumns, RejectReasonColumn]
    : hooAccommodationsColumns;
  const columns = showActions ? [...columnsWithRejectReason, actionColumn] : columnsWithRejectReason;
  return columns;
}
